import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import SubmitTemplateCard from '@/components/common/SubmitTemplateCard';
import TemplateCard, { TemplateCardData } from '@/components/common/TemplateCard';
import CustomContainer from '@/components/ui/CustomContainer';
import ProBadge from '@/components/ui/ProBadge';
import { MultiLanguageUrl } from '@/constants/multiLanguage.constant';
import { handleResetAppearance } from '@/features/appearance/appearanceSlice';
import { selectClient } from '@/features/dashboard.slice';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import Utilities from '@/utils';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import { resetCreateFlow, selectCurrentDataState, selectCurrentTypeState, toggeleCreateModal } from '../create.slice';
import { useGetTemplates } from '../hooks/useGetTemplates';
import ChatbotName from './ChatbotName';

function Templates({ BotType }: { BotType: BotTypeEnum }) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isLoading, isError, isSuccess, data, refetch } = useGetTemplates(BotType);

  const client = useAppSelector(selectClient);
  const currentData = useAppSelector(selectCurrentDataState);
  const currentType = useAppSelector(selectCurrentTypeState);
  const canCreateMore = Utilities.useCanCreateMoreChatbots();

  const { setValue, formState, getValues, reset, watch } = useFormContext();

  const result = useMemo(() => {
    return currentData?.bot_code == BotTypeEnum.CALLBOT
      ? data?.filter(x => x.code === 'call_bot')
      : data?.filter(x => x.code !== 'call_bot' && x.code !== 'self_clone') || [];
  }, [currentData, isSuccess]);

  const currentTemplateId = BotType == BotTypeEnum.AI_READY_TO_USE ? getValues('assistant') : getValues('template_id');

  useEffect(() => {
    if (currentType === BotTypeEnum.CALLBOT) {
      if (result && result?.length > 0) {
        const item = result[0];
        if (hasRequiredPermission(item.code)) {
          setValue('assistant', item.code, {
            shouldValidate: true,
          });
          setValue('required_instructions', item.required_instructions);
        }
      }
    } else if (BotType == BotTypeEnum.AI_READY_TO_USE) {
      if (hasRequiredPermission('free')) {
        setValue('assistant', currentData?.assistant || 'free', { shouldValidate: true });
      }
    } else {
      if (canCreateMore) {
        setValue('template_id', currentData?.template_id || 1);
      }
    }
  }, [isSuccess, result]);

  const onSelectTemplate = (item: any) => {
    if (!item.hasRequiredPermission || Boolean(canCreateMore)) {
      setValue('template_id', item.package_id || item.id, {
        shouldValidate: true,
      });
      if (item.package_id) {
        setValue('package', item, { shouldValidate: true });
        setValue('has_package', true, { shouldValidate: true });
      } else {
        setValue('package', null, { shouldValidate: true });
        setValue('has_package', false, { shouldValidate: true });
      }
    }
  };
  const hasRequiredPermission = (assistantType: string) => {
    const targetBotCofig = client?.botConfigs.find(item => item.botName === (currentType === BotTypeEnum.CALLBOT ? currentType : BotType));

    if (targetBotCofig) {
      const acceptableModels = targetBotCofig.config.models;

      return acceptableModels ? acceptableModels.includes(assistantType) : true;
    }
  };

  const renderScenarioTemplates = () => {
    return (
      <div className="md:tw-flex tw-w-full tw-h-full ">
        <div className="tw-grid  xxl:tw-grid-cols-5 xl:tw-grid-cols-4 lg:tw-grid-cols-3 md:tw-grid-cols-2 tw-grid-cols-1 tw-gap-4">
          {[...(data || [])]
            .sort((a, b) => {
              // Keep "from scratch" template (id=1) always first
              if (a.id === 1) return -1;
              if (b.id === 1) return 1;
              // Sort rest by download count descending
              return (b?.allTransactionCount || 0) - (a?.allTransactionCount || 0);
            })
            .map((item: any, index: number) => {
              const items = [];

              // Add the template card
              items.push(
                <TemplateCard
                  canCreateMore={Boolean(canCreateMore)}
                  key={`template-${index}`}
                  item={{ ...item, downloadCount: item?.allTransactionCount }}
                  isSelected={currentTemplateId == (item?.package_id || item.id)}
                  isDisabled={!canCreateMore && item.hasRequiredPermission}
                  onClick={onSelectTemplate}
                />
              );

              // Add submit template block every 6th item (after indices 5, 11, 17, etc.)
              if ((index + 1) % 6 === 0) {
                items.push(
                  <SubmitTemplateCard
                    key={`submit-${index}`}
                    onClickAction={() => {
                      dispatch(resetCreateFlow());
                      dispatch(toggeleCreateModal(false));
                      dispatch(handleResetAppearance());
                      reset();
                      navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel/marketplace`);
                    }}
                    isSelected={currentTemplateId === 100}
                  />
                );
              }

              return items;
            })
            .flat()}
        </div>
      </div>
    );
  };

  // const onSelectQuickBotTemplate = (item: TemplateCardData) => {
  //   if (hasRequiredPermission(item.code!)) {
  //     setValue('assistant', item.code, {
  //       shouldValidate: true,
  //     });
  //     setValue('required_instructions', (item as any).required_instructions);
  //   }
  // };

  const renderQuickBotTemplates = () => {
    return (
      <div className="tw-grid tw-grid-cols-12 tw-gap-5">
        {result?.map((item, index: number) => (
          <div
            key={index}
            className={`lg:tw-col-span-3 md:tw-col-span-6 tw-col-span-12 tw-w-full tw-bg-white tw-shrink tw-shadow tw-flex-col tw-justify-start tw-items-start tw-inline-flex tw-border-solid tw-rounded-lg tw-relative ${
              currentTemplateId === item.code ? 'tw-border-4 tw-border-blue-500' : 'tw-border-4 tw-border-transparent'
            } ${!hasRequiredPermission(item.code) ? 'tw-cursor-not-allowed' : ''}`}
            onClick={() => {
              if (hasRequiredPermission(item.code)) {
                setValue('assistant', item.code, {
                  shouldValidate: true,
                });
                setValue('required_instructions', item.required_instructions);
              }
            }}
            onKeyDown={() => {
              if (hasRequiredPermission(item.code)) {
                setValue('assistant', item.code, { shouldValidate: true });
                setValue('required_instructions', item.required_instructions);
              }
            }}
            id={`btn-assistant-item-${item.code}`}
            role="button"
            aria-label="assistant-selector"
            tabIndex={0}
          >
            <div className="tw-self-stretch tw-h-[200px] tw-bg-blue-200 tw-rounded-tl-lg tw-rounded-tr-lg tw-relative">
              {!hasRequiredPermission(item.code) && (
                <div className="tw-absolute tw-top-3 tw-right-3">
                  <ProBadge
                    onClose={() => {
                      dispatch(resetCreateFlow());
                      reset();
                    }}
                    filled
                  />
                </div>
              )}
              <img
                className="img-fluid tw-object-cover tw-h-[200px] tw-w-full tw-rounded-tl-lg tw-rounded-tr-lg tw-object-top"
                src={item.images?.main}
                alt={item.code}
              />
            </div>
            <div className="tw-self-stretch tw-px-6 tw-pt-4 tw-pb-8 tw-rounded-bl-lg tw-rounded-br-lg tw-flex-col tw-justify-start tw-items-start tw-gap-3 flex">
              <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
                <div className="tw-text-gray-900 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">{item.name}</div>
                <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{item.description}</div>
              </div>
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mt-3">
                {item.skills?.map(sk => (
                  <div key={sk} className="tw-px-2.5 tw-py-0.5 tw-bg-blue-50 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex">
                    <div className="tw-text-center tw-text-blue-800 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{sk}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <section>
      <div className="md:tw-px-8 tw-px-1 tw-flex-col tw-justify-start tw-tw-items-start tw-gap-8 tw-inline-flex">
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
          {(BotType == BotTypeEnum.TEXT_SCENARIO || BotType == BotTypeEnum.VIDEO_SCENARIO) && (watch('has_package') === true || !canCreateMore) ? (
            <></>
          ) : (
            <ChatbotName />
          )}

          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
            <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
              {BotType == BotTypeEnum.AI_READY_TO_USE ? t('Wizard/AssistantTemplates/key1') : t('TextScenarioSetupChatbot/title')}
            </div>
            <div className="tw-self-stretch tw-text-gray-900 text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
              {BotType == BotTypeEnum.AI_READY_TO_USE ? t('Wizard/AssistantTemplates/key2') : t('TextScenarioSetupChatbot/description')}
            </div>
          </div>

          {formState.errors.template_id && (
            <div className="tw-self-stretch tw-text-red-600 tw-text-xs tw-font-['Lexend'] tw-leading-none tw-block tw-mt-5">
              {formState.errors?.template_id?.message as string}
            </div>
          )}

          {formState.errors.assistant && (
            <div className="tw-self-stretch tw-text-red-600 tw-text-xs tw-font-['Lexend'] tw-leading-none tw-block tw-mt-5">
              {formState.errors?.assistant?.message as string}
            </div>
          )}

          <CustomContainer {...{ isError, isLoading, isSuccess, refetch }}>
            {(BotType == BotTypeEnum.TEXT_SCENARIO || BotType == BotTypeEnum.VIDEO_SCENARIO) && renderScenarioTemplates()}

            {BotType == BotTypeEnum.AI_READY_TO_USE && renderQuickBotTemplates()}
          </CustomContainer>
        </div>
      </div>
    </section>
  );
}

export default WrappedErrorBoundary(Templates);
